import { Accordion, Card, Flex, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useFolderActionsRedux } from '../hooks';
import { Folder } from '../types';
import { FileItem } from './FileItem';
import { FolderActions } from './FolderActions';
import { FolderHeader } from './FolderHeader';
import { RenderFields } from './RenderFields';
import { AddFileModal, DeleteFolderModal } from './Modals';
import { useNavigate } from 'react-router';
import routes from '@/commons/routes';
import { FilterField } from '@/users/pages/UserFilters/types/FilterField';

interface FolderCardProps {
  folder: Folder;
  customFieldBasedFilters?: FilterField[];
  /** Called after archive, status change, or delete succeeds (e.g. refetch server-paged lists). */
  onFolderMutationComplete?: () => void;
  /** Server-driven lists: show all filter columns; list filtering is not done in the UI. */
  showAllCustomFields?: boolean;
}

export const FolderCard = ({
  folder,
  customFieldBasedFilters = [],
  onFolderMutationComplete,
  showAllCustomFields = false,
}: FolderCardProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [addFileModalOpened, setAddFileModalOpened] = useState(false);
  const { handleArchive, handleStatusChange, handleDelete } =
    useFolderActionsRedux(folder, {
      onMutationComplete: onFolderMutationComplete,
    });

  return (
    <>
      {deleteModalOpened && (
        <DeleteFolderModal
          opened={deleteModalOpened}
          onClose={() => setDeleteModalOpened(false)}
          folderName={folder.folderName}
          fileCount={folder.files.length}
          onConfirm={handleDelete}
        />
      )}

      {addFileModalOpened && (
        <AddFileModal
          opened={addFileModalOpened}
          onClose={() => setAddFileModalOpened(false)}
          folderCategory={folder.category}
          folderName={folder.folderName}
          folderFiles={folder.files}
        />
      )}

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Flex direction="column" gap="xs">
          {customFieldBasedFilters.length > 0 && (
            <RenderFields
              folder={folder}
              customFieldBasedFilters={customFieldBasedFilters}
              showAllCustomFields={showAllCustomFields}
            />
          )}

          <FolderActions
            folder={folder}
            onAddFile={() => setAddFileModalOpened(true)}
            onOpen={() =>
              navigate(routes.routes.clientFolder(folder.folderName))
            }
            onArchive={handleArchive}
            onStatusChange={handleStatusChange}
            onDelete={() => setDeleteModalOpened(true)}
          />

          <FolderHeader folder={folder} />

          <Accordion variant="contained" defaultValue="files">
            <Accordion.Item value="files">
              <Accordion.Control>
                <Text size="sm" c="dimmed">
                  {intl.formatMessage({ id: 'files' })}
                </Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="xs">
                  {folder.files.map((file, fileIndex) => (
                    <FileItem
                      key={`${folder.id}-${fileIndex}-${String(file.id ?? '')}-${file.name ?? ''}`}
                      folder={folder}
                      file={file}
                      availableFiles={folder.files}
                      existingFileNames={folder.files.map((f) => f.name)}
                    />
                  ))}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Flex>
      </Card>
    </>
  );
};
